import { Button, TableItem } from 'components/atoms';
import Popup from 'components/molecules/popup';
import LabeledFingerprintSubmissionStatus from 'components/molecules/labeledStatus/LabeledFingerprintSubmissionStatus';
import { Table } from 'components/organisms';
import { fingerprintNotValidScore } from 'constants/fingerprint';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useParamId } from 'hooks/useParamId';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiFingerprint, PiMagnifyingGlass, PiQuestion } from 'react-icons/pi';
import {
  useGetTrackFingerprintLookup,
  useGetTrackFingerprintSubmissions,
  usePostTrackFingerprintSubmission,
} from 'services/tracks/fingerprint';
import { ITrackMutationForm } from 'services/tracks/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDateAndHour } from 'utils/global/date.utils';
import { useQueryClient } from '@tanstack/react-query';

const TrackFingerprint = () => {
  const trackId = useParamId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const submissions = useGetTrackFingerprintSubmissions(trackId);
  const lookup = useGetTrackFingerprintLookup(trackId, false);
  const submitFingerprint = usePostTrackFingerprintSubmission();

  const { values, setFieldValue }: IFormikContextType<ITrackMutationForm> =
    useFormikContext<ITrackMutationForm>();

  const score = values.matchFingerprintScore;
  const isVerified = score !== null;

  const scoreColor = useMemo(
    () =>
      !isVerified || score === null || score < fingerprintNotValidScore * 0.8
        ? 'var(--primary)'
        : score < fingerprintNotValidScore
          ? 'var(--orange)'
          : 'var(--red)',
    [isVerified, score]
  );

  const isSubmitDisabled =
    !isVerified ||
    score === null ||
    score >= fingerprintNotValidScore ||
    !!submissions.data?.length ||
    values.waitingForAcoustIdSubmission ||
    submitFingerprint.isPending;

  const handleLookup = async () => {
    const result = await lookup.refetch();

    if (result.isSuccess && result.data) {
      setFieldValue('matchFingerprintScore', result.data.score);
      setFieldValue('hasMatchFingerprint', result.data.match);
      await queryClient.invalidateQueries({ queryKey: ['track', trackId] });
    }
  };

  const handleSubmit = () => {
    submitFingerprint.mutate(trackId);
  };

  return (
    <div className='w-full h-full flex flex-col gap-4'>
      <div className=' w-full flex gap-2 max-w-[700px]'>
        <Button
          variant={BUTTON_VARIANT.white}
          onClick={handleLookup}
          fullWidth
          isLoading={lookup.isFetching}
          disabled={isVerified || lookup.isFetching}>
          <Button.Icon icon={<PiMagnifyingGlass />} />
          <Button.Text text={t('track.fingerprint.lookup.button')} />
        </Button>
        <Popup.WarningPopup
          title={t('track.fingerprint.lookup.button')}
          message={t('track.fingerprint.lookup.help')}
          trigger={
            <Button variant={BUTTON_VARIANT.white} onlyIcon>
              <Button.Description
                description={t('track.fingerprint.lookup.help')}
              />
              <Button.Icon icon={<PiQuestion />} />
            </Button>
          }
        />
      </div>
      <div
        className='p-[32px] pb-[16px] rounded-sm w-full flex flex-col gap-2 max-w-[700px]'
        style={{
          backgroundColor: 'var(--background)',
        }}>
        <p className='pb-[24px] w-full text-center font-medium'>
          {!isVerified
            ? t('track.fingerprint.not-verified')
            : score < fingerprintNotValidScore
              ? t('track.fingerprint.success')
              : t('track.fingerprint.failure')}
        </p>
        <div
          className='rounded-sm w-full'
          style={{ backgroundColor: 'var(--app-background)' }}>
          <div
            className='relative rounded-sm h-[10px]'
            style={{
              backgroundColor: scoreColor,
              width: `${Math.ceil(score ?? 0)}%`,
            }}>
            {isVerified && score !== null && (
              <span
                className='absolute right-[-10px] py-4 px-8 rounded-sm font-bold text-white flex items-center justify-center top-1/2 -translate-y-1/2'
                style={{ backgroundColor: scoreColor }}>
                {score}%
              </span>
            )}
          </div>
        </div>
        <div className='flex justify-between w-full font-bold opacity-50 '>
          <p>0%</p>
          <p>50%</p>
          <p>100%</p>
        </div>
      </div>
      <div className=' w-full flex gap-2 max-w-[700px]'>
        <Button
          variant={BUTTON_VARIANT.submit}
          fullWidth
          onClick={handleSubmit}
          isLoading={submitFingerprint.isPending}
          disabled={isSubmitDisabled}>
          <Button.Icon icon={<PiFingerprint />} />
          <Button.Text text={t('track.fingerprint.submit.button')} />
        </Button>
        <Popup.WarningPopup
          title={t('track.fingerprint.submit.button')}
          message={t('track.fingerprint.submit.help')}
          trigger={
            <Button variant={BUTTON_VARIANT.submit} onlyIcon>
              <Button.Description
                description={t('track.fingerprint.submit.help')}
              />
              <Button.Icon icon={<PiQuestion />} />
            </Button>
          }
        />
      </div>
      <Table
        id={'TrackFingerprintSubmissions'}
        size='compact'
        data={{
          nodes: submissions.data ?? [],
        }}
        columns={[
          {
            label: t('track.fingerprint.table.acoust-id'),
            renderCell: ({ acoustId }) => (
              <TableItem>
                <TableItem.Text text={acoustId} />
              </TableItem>
            ),
          },
          {
            label: t('track.fingerprint.table.status'),
            renderCell: ({ status }) => (
              <LabeledFingerprintSubmissionStatus status={status.id} />
            ),
          },
          {
            label: t('track.fingerprint.table.created-at'),
            renderCell: ({ createdAt }) => (
              <TableItem>
                <TableItem.Text text={formatDateAndHour(createdAt)} />
              </TableItem>
            ),
          },
        ]}
      />
    </div>
  );
};

export default TrackFingerprint;
